import type { Citation } from '@ctw/shared/api/ai/types';
import { getAllergyIntolerancesById } from '@ctw/shared/api/fhir/allergies';
import { useBinaryId } from '@ctw/shared/api/fhir/binaries';
import { fetchConditionsByIdFQS } from '@ctw/shared/api/fhir/conditions';
import { fetchCoveragesByIdFQS } from '@ctw/shared/api/fhir/coverages';
import { getDocumentReferenceWithBasicsById } from '@ctw/shared/api/fhir/document';
import { getMedicationStatementsByIdFQS } from '@ctw/shared/api/fhir/medications';
import type { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { CoverageModel } from '@ctw/shared/api/fhir/models/coverage';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import type { ProcedureModel } from '@ctw/shared/api/fhir/models/procedure';
import { fetchObservationsById } from '@ctw/shared/api/fhir/observations';
import { getPatientByID } from '@ctw/shared/api/fhir/patient-helper';
import { fetchProceduresById } from '@ctw/shared/api/fhir/procedure';
import { useOpenSourceDocument } from '@ctw/shared/content/CCDA/use-open-source-document';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useMemo } from 'react';
import { CitationComponent } from './citation';

export type SupportedCitationLinkResourceType =
  | Array<AllergyModel>
  | Array<ConditionModel>
  | Array<CoverageModel>
  | Array<DocumentModel>
  | Array<MedicationStatementModel>
  | Array<ObservationModel>
  | Array<PatientModel>
  | Array<ProcedureModel>
  | null;

export type CitationLinkProps = {
  resourceInfo: { resourceID: string; resourceType: string };
  citation: Citation;
};

export const CitationLink = ({ resourceInfo, citation }: CitationLinkProps) => {
  const telemetry = useTelemetry();
  const shouldUseFqsDocumentHotstorage = useFeatureFlag('ctw-fqs-use-document-hotstorage');
  const shouldUseFqsObservationHotstorage = useFeatureFlag('ctw-fqs-use-observation-hotstorage');
  const { isLoading, isError, data } = usePatientQuery<SupportedCitationLinkResourceType>({
    queryId: 'ResourceByTypeAndIDQuery',
    queryKey: [resourceInfo.resourceID, resourceInfo.resourceType],
    queryFn: async ({
      graphqlClient,
      telemetry: queryTelemetry,
      patient,
      ctwFetch,
      requestContext,
    }) => {
      switch (resourceInfo.resourceType) {
        case 'AllergyIntolerance':
          return await getAllergyIntolerancesById(queryTelemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'Condition':
          return await fetchConditionsByIdFQS(queryTelemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'DocumentReference':
          return await getDocumentReferenceWithBasicsById(
            queryTelemetry,
            graphqlClient,
            patient,
            [resourceInfo.resourceID],
            {
              requestHeaders: {
                'X-Use-Hotstorage': shouldUseFqsDocumentHotstorage ? '1' : '0',
              },
            },
          );
        case 'MedicationStatement':
          return await getMedicationStatementsByIdFQS(queryTelemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'Observation':
          return await fetchObservationsById(
            queryTelemetry,
            graphqlClient,
            patient,
            [resourceInfo.resourceID],
            {
              requestHeaders: {
                'X-Use-Hotstorage': shouldUseFqsObservationHotstorage ? '1' : '0',
              },
            },
          );
        case 'Procedure':
          return await fetchProceduresById(queryTelemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'Coverage':
          return await fetchCoveragesByIdFQS(queryTelemetry, graphqlClient, patient, [
            resourceInfo.resourceID,
          ]);
        case 'Patient':
          return [await getPatientByID(ctwFetch, requestContext, resourceInfo.resourceID)];
        default:
          return Promise.resolve(null);
      }
    },
  });

  const binaryIdQuery = useBinaryId(data?.[0], !isLoading, true);
  const binaryId = useMemo(() => binaryIdQuery.data, [binaryIdQuery.data]);
  const { openSourceDocumentById } = useOpenSourceDocument();
  const { openResourceDrawer } = useResourceDrawer();

  if (isLoading || isError || !data) {
    return null;
  }

  // if it's not an array, it's not a valid resource
  if (!Array.isArray(data)) {
    return null;
  }

  if (data.length === 0) {
    return null;
  }

  const resource = data[0];

  const trackCitationLinkClick = () => {
    telemetry.trackInteraction('citation_link_click', {
      citationNumber: citation.id,
    });
  };

  switch (resource.resourceType) {
    case 'AllergyIntolerance': {
      const allergy = resource as AllergyModel;

      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();
              void openSourceDocumentById(binaryId);
            }
            return openResourceDrawer({ telemetry, model: allergy });
          }}
        />
      );
    }
    case 'Condition': {
      const condition = resource as ConditionModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();
              void openSourceDocumentById(binaryId);
            }
            return openResourceDrawer({ telemetry, model: condition });
          }}
        />
      );
    }
    case 'DocumentReference': {
      const document = resource as DocumentModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();
              void openSourceDocumentById(binaryId);
              return;
            }
            return openResourceDrawer({ telemetry, model: document });
          }}
        />
      );
    }
    case 'MedicationStatement': {
      const medication = resource as MedicationStatementModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();
              void openSourceDocumentById(binaryId);
            }
            return openResourceDrawer({ telemetry, model: medication });
          }}
        />
      );
    }
    case 'Observation': {
      const observation = resource as ObservationModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();
              void openSourceDocumentById(binaryId);
            }
            return openResourceDrawer({ telemetry, model: observation });
          }}
        />
      );
    }
    case 'Procedure': {
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();
              void openSourceDocumentById(binaryId);
            }
            // TODO: open procedure drawer
            return null;
          }}
        />
      );
    }
    case 'Patient': {
      const patient = resource as PatientModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();
              void openSourceDocumentById(binaryId);
            }
            return openResourceDrawer({ telemetry, model: patient });
          }}
        />
      );
    }
    case 'Coverage': {
      const coverage = resource as CoverageModel;
      return (
        <CitationComponent
          citation={citation}
          openDetails={() => {
            if (binaryId) {
              trackCitationLinkClick();
              void openSourceDocumentById(binaryId);
            }
            return openResourceDrawer({ telemetry, model: coverage });
          }}
        />
      );
    }
    default:
      return null;
  }
};
